// The six assessment phases. Extracted from HowItWorks.js (PRD-171B WP8) so the
// HowItWorks detail cards AND the shared <PhaseFlow> diagram (HowItWorks + the
// landing page) read from ONE source of truth and cannot drift.
// HowItWorks consumes the full objects (subtitle/description/capabilities/screenshot);
// PhaseFlow needs only number/title.
export const phases = [
  {
    number: '01',
    title: 'Asset Discovery',
    subtitle: 'Map Your Entire External Perimeter',
    description: 'Before you can defend your organization, you need complete visibility. Our discovery engine maps every subdomain, IP address, open port, and running service — just like an attacker would during initial reconnaissance.',
    capabilities: [
      'Subdomain enumeration',
      'IP address resolution',
      'Port scanning',
      'Technology fingerprinting',
      'Cloud infrastructure mapping',
    ],
    screenshot: '/images/screenshots/scan-result-overview',
    screenshotAlt: 'Scan result overview showing target profile, risk score, scan statistics, and DriftAlarm Brief',
  },
  {
    number: '02',
    title: 'Vulnerability Assessment',
    subtitle: 'Find Weaknesses Before Adversaries Do',
    description: 'Multi-engine vulnerability detection correlates findings across industry-standard scanners and 10,000+ Nuclei templates to identify CVEs, misconfigurations, default credentials, and exposed services.',
    capabilities: [
      'CVE detection with severity scoring',
      'CISA Known Exploited Vulnerabilities (KEV) correlation',
      'Configuration weakness identification',
      'Default credential detection',
      'Service-specific vulnerability checks',
    ],
    screenshot: '/images/screenshots/attack-surface-analysis-v2',
    screenshotAlt: 'Attack surface analysis showing exposed services, entry points, critical assets, and critical technologies',
    // PRD-180: 2x re-capture opts this phase's ScreenshotFrame into the retina
    // srcSet (the @2x twins exist on disk). Only this phase sets it.
    retina: true,
  },
  {
    number: '03',
    title: 'AI Threat Analysis',
    subtitle: 'Intelligence, Not Just Data',
    description: 'Raw vulnerability lists aren\'t actionable. Our AI analysis engine generates executive-ready security briefs that contextualize findings through real-world attack patterns, assign threat levels, and rate attack complexity.',
    capabilities: [
      'AI-generated executive summary with confidence score',
      'Threat level assessment (Critical / High / Medium / Low)',
      'Attack complexity rating',
      'Misconfiguration categorization',
      'Information leakage identification',
    ],
    screenshot: '/images/screenshots/da-brief',
    screenshotAlt: 'DriftAlarm Brief showing CRITICAL threat level, 95% AI confidence, and executive summary',
  },
  {
    number: '04',
    title: 'Framework Mapping',
    subtitle: 'Align to Industry Standards',
    description: 'Every finding is mapped to the frameworks your auditors, board, and compliance team care about. Communicate risk in a language stakeholders understand.',
    capabilities: [
      'MITRE ATT&CK technique mapping (8+ techniques per scan)',
      'OWASP Top 10 alignment',
      'CIS Controls mapping',
      'NIST Cybersecurity Framework correlation',
      'Kill chain stage identification',
    ],
    screenshot: '/images/screenshots/mitre-attack-mapping',
    screenshotAlt: '8 MITRE ATT&CK techniques mapped including T1190 Exploit Public-Facing Application',
    secondaryScreenshot: '/images/screenshots/compliance-mapping',
    secondaryAlt: 'OWASP Top 10, CIS Controls, and NIST CSF compliance mapping',
  },
  {
    number: '05',
    title: 'Remediation Roadmap',
    subtitle: 'Fix the Right Things in the Right Order',
    description: 'Every scan produces a prioritized remediation plan ranked by severity, effort, business impact, and timeline. Recommendations include specific attack vectors and technology-specific fix instructions — not generic advice.',
    capabilities: [
      'Priority-ranked remediation (Critical → Low)',
      'Effort and timeline estimates (Immediate / 24-48h / Week / Month)',
      'Attack vector descriptions per finding',
      'Technology-specific fix instructions',
      'Defensive recommendations by category (Application, Network, Infrastructure, Cloud, Monitoring)',
    ],
    screenshot: '/images/screenshots/prioritized-recommendations',
    screenshotAlt: '4 ranked remediation recommendations with severity, effort, impact, and timeline',
    secondaryScreenshot: '/images/screenshots/defensive-recommendations',
    secondaryAlt: '5 defensive recommendations organized by category with effort ratings',
  },
  {
    number: '06',
    title: 'Continuous Drift Detection',
    subtitle: 'Watch for Changes That Create Risk',
    description: 'Security isn\'t a point-in-time assessment — it\'s continuous awareness. DriftAlarm monitors 41 conditions across 8 categories, alerting you the moment your attack surface changes. New ports, expired certificates, DNS modifications, technology changes — nothing drifts unnoticed.',
    capabilities: [
      '41 detection rules across 8 categories',
      'Certificate management monitoring',
      'DNS and registration change tracking',
      'Network port and service monitoring',
      'Technology stack drift detection',
      'Compliance readiness monitoring',
    ],
    screenshot: '/images/screenshots/drift-alarm-rules',
    screenshotAlt: 'Drift alarm rule categories showing Certificate Management, Compliance, DNS, and Essential Security rules',
  },
];

export default phases;
